import { Edit, Mail, MessageSquare, Send, Shield, Trash2, Zap } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import type { ContactWithStatus } from '@/types/contacts';
import { ContactStatusBadge } from './ContactStatusBadge';

interface ContactsTableProps {
  contacts: ContactWithStatus[];
  onEdit: (contact: ContactWithStatus) => void;
  onDelete: (contact: ContactWithStatus) => void;
  onVerify: (contact: ContactWithStatus) => void;
  onConnectTelegram: (contact: ContactWithStatus) => void;
  onSendTest: (contact: ContactWithStatus) => void;
}

export function ContactsTable({
  contacts,
  onEdit,
  onDelete,
  onVerify,
  onConnectTelegram,
  onSendTest,
}: ContactsTableProps) {
  const getDestination = (contact: ContactWithStatus) => {
    if (contact.type === 'email') {
      return contact.email || '';
    }
    if (contact.type === 'telegram') {
      return contact.telegram_chat_id || 'Not linked';
    }
    return contact.phone_number || '';
  };

  const canVerify = (contact: ContactWithStatus) => {
    return (
      contact.type !== 'telegram' &&
      contact.verification_status !== 'verified' &&
      !contact.blocked_at
    );
  };

  const getVerifyButtonText = (contact: ContactWithStatus) => {
    if (contact.verification_status === 'requested') {
      return 'Enter Code';
    }
    if (contact.verification_status === 'not_verified' && contact.verification_attempts > 0) {
      return 'Resend';
    }
    return 'Verify';
  };

  const needsTelegramLink = (contact: ContactWithStatus) => {
    return contact.type === 'telegram' && !contact.verified_at && !contact.blocked_at;
  };

  const canSendTest = (contact: ContactWithStatus) => {
    if (contact.blocked_at) return { ok: false, reason: 'Blocked' };
    if (contact.type === 'email' && contact.monitoringEmailStatus === 'unsubscribed') {
      return { ok: false, reason: 'Monitoring emails unsubscribed' };
    }
    if (contact.type === 'phone' && !contact.phone_number) {
      return { ok: false, reason: 'Missing phone number' };
    }
    if (contact.type === 'phone') return { ok: false, reason: 'SMS delivery is unavailable' };
    if (!contact.verified_at) return { ok: false, reason: 'Not verified' };
    if (contact.type === 'email' && !contact.email) {
      return { ok: false, reason: 'Missing email' };
    }
    if (contact.type === 'telegram' && !contact.telegram_chat_id) {
      return { ok: false, reason: 'Telegram not linked' };
    }
    return { ok: true };
  };

  const getVerifyButtonDisabled = (contact: ContactWithStatus) => {
    if (contact.type === 'phone') {
      return true;
    }
    if (contact.blocked_at) {
      return true;
    }
    if (contact.next_allowed_at) {
      return new Date() < new Date(contact.next_allowed_at);
    }
    return false;
  };

  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Name</TableHead>
          <TableHead>Type</TableHead>
          <TableHead>Destination</TableHead>
          <TableHead>Status</TableHead>
          <TableHead className="text-right">Actions</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {contacts.length === 0 ? (
          <TableRow>
            <TableCell colSpan={5} className="text-center text-gray-500">
              No contacts found. Add your first contact to get started.
            </TableCell>
          </TableRow>
        ) : (
          contacts.map((contact) => (
            <TableRow key={contact.id}>
              <TableCell className="font-medium">{contact.name}</TableCell>
              <TableCell>
                <div className="flex items-center gap-2">
                  {contact.type === 'email' ? (
                    <>
                      <Mail className="h-4 w-4 text-gray-400" />
                      Email
                    </>
                  ) : contact.type === 'telegram' ? (
                    <>
                      <Send className="h-4 w-4 text-gray-400" />
                      Telegram
                    </>
                  ) : (
                    <>
                      <MessageSquare className="h-4 w-4 text-gray-400" />
                      SMS
                    </>
                  )}
                </div>
              </TableCell>
              <TableCell className="font-mono text-sm">{getDestination(contact)}</TableCell>
              <TableCell>
                <ContactStatusBadge status={contact.verification_status} />
                {contact.blocked_at && <span className="ml-2 text-xs text-red-600">Blocked</span>}
                {contact.type === 'email' && contact.monitoringEmailStatus === 'unsubscribed' && (
                  <p className="mt-1 text-xs text-amber-700">Monitoring emails unsubscribed</p>
                )}
                {contact.type === 'phone' && (
                  <p className="mt-1 text-xs text-gray-500">SMS delivery unavailable</p>
                )}
              </TableCell>
              <TableCell className="text-right">
                <div className="flex justify-end gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => onSendTest(contact)}
                    disabled={!canSendTest(contact).ok}
                    title={
                      canSendTest(contact).ok ? 'Send test message' : canSendTest(contact).reason
                    }
                  >
                    <Zap className="h-4 w-4 mr-1" />
                    Test
                  </Button>
                  {needsTelegramLink(contact) && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => onConnectTelegram(contact)}
                      title="Connect Telegram"
                    >
                      <Send className="h-4 w-4 mr-1" />
                      Connect
                    </Button>
                  )}
                  {canVerify(contact) && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => onVerify(contact)}
                      disabled={getVerifyButtonDisabled(contact)}
                      title={
                        contact.type === 'phone'
                          ? 'SMS verification is unavailable'
                          : contact.next_allowed_at
                            ? `Available at ${new Date(contact.next_allowed_at).toLocaleTimeString()}`
                            : ''
                      }
                    >
                      <Shield className="h-4 w-4 mr-1" />
                      {getVerifyButtonText(contact)}
                    </Button>
                  )}
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => onEdit(contact)}
                    aria-label={`Edit contact ${contact.name}`}
                  >
                    <Edit className="h-4 w-4" />
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => onDelete(contact)}
                    aria-label={`Delete contact ${contact.name}`}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </TableCell>
            </TableRow>
          ))
        )}
      </TableBody>
    </Table>
  );
}
